<template>
  <div>
    <v-card class="pa-10" elevation="0">
      <v-row>
        <v-col>
          <v-row no-gutters align="center">
            <h4 class="greyDark--text font-weight-bold mb-0">
              {{ $t('common:documentation_plural') }}
            </h4>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs}">
                <v-btn class="ml-2" icon @click="cardView = !cardView" v-bind="attrs" v-on="on">
                  <v-icon>list_alt</v-icon>
                </v-btn>
              </template>
              <span v-if="cardView">{{ $t('buttons:table-view') }}</span>
              <span v-else>{{ $t('buttons:card-view') }}</span>
            </v-tooltip>
          </v-row>
        </v-col>
        <v-col class="d-none d-md-block">
          <RadialScore ref="roleScore" :score="score" score-icon="document"/>
        </v-col>
        <v-col class="text-right">
          <BaseButton
              color="primary"
              :btnText="$t('buttons:add-specific', { specific: $t('common:documentation') })"
              @clickedButton="dialog = true"
          />
        </v-col>
        <v-col cols="12" sm="12" class="d-block d-md-none">
          <RadialScore ref="roleScore" :score="score" score-icon="document"/>
        </v-col>
        <v-col cols="12" class="pt-0" v-if="!cardView">
          <DataTablePaginated
              ref="tableDocuments"
              :headers="docuHeaders"
              @updateTableData="updateDocuTable"
              icon="visibility"
              icon2="cloud_download"
              :icon3="isLoggedIn ? 'delete' : null"
              :iconTooltip="$t('buttons:show')"
              :iconTooltip2="$t('buttons:download')"
              :iconTooltip3="$t('buttons:delete')"
              @clickedButton1="viewDocumentation"
              @clickedButton2="downloadDocumentation"
              @clickedButton3="deleteDocumentation"
              sortBy="translatedType"
              @cacheCommit="$store.commit('book/facility/storeFacilityDocumentationsSearch', $event)"
              :cachedSearch="$store.state.book.facility.documentationsSearch"
          />
        </v-col>
      </v-row>

      <div v-show="cardView">

        <!-- Docu toggle button to only show specific types -->
        <v-row>
          <v-col>
            <v-btn-toggle v-model="docuToggle">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs}">
                  <v-btn
                      value="ALL"
                      icon
                      v-bind="attrs" v-on="on"
                      color="greyMedium"
                  >
                    <v-icon color="greyMedium">select_all</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('book/public:qrcode.data.docus.filter-btns.all') }}</span>
              </v-tooltip>

              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs}">
                  <v-btn
                      value="DOCUMENTS"
                      icon
                      v-bind="attrs" v-on="on"
                      color="greyMedium"
                  >
                    <v-icon color="greyMedium">description</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('book/public:qrcode.data.docus.filter-btns.documents') }}</span>
              </v-tooltip>

              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs}">
                  <v-btn
                      value="STATE_DOCUMENTS"
                      icon
                      v-bind="attrs" v-on="on"
                      color="greyMedium"
                  >
                    <v-icon color="greyMedium">insights</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('book/public:qrcode.data.docus.filter-btns.state-documents') }}</span>
              </v-tooltip>

              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs}">
                  <v-btn
                      value="IMAGES"
                      icon
                      v-bind="attrs" v-on="on"
                      color="greyMedium"
                  >
                    <v-icon color="greyMedium">image</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('book/public:qrcode.data.docus.filter-btns.images') }}</span>
              </v-tooltip>
            </v-btn-toggle>
          </v-col>
        </v-row>

        <SearchAndSortBar
            @updateData="updateDocuTable"
            @cacheCommit="$store.commit('book/facility/storeFacilityDocumentationsSearch', $event)"
            :cachedSearch="$store.state.book.facility.documentationsSearch"
            class="mb-3"
            :sorts="docuHeaders"
            :filters="dataFilters"
            filterValueField="name"
            filterTextField="translatedName"
            default-sort-by="translatedType"
        />

        <v-row>
          <v-col v-show="docuToggle === 'ALL' || docuToggle === 'DOCUMENTS'" cols="12" md="6" xl="4" v-for="item in requiredDocus" :key="item.id">
            <DragAndDropItemCard
                :title="item.translatedName"
                required
                height="400"
                @fileChange="addRequiredDocu($event, item)"
                :priority="item.priority"
            />
          </v-col>
          <v-col cols="12" md="6" xl="4" v-for="item in restDocu.content" :key="item.id" >
            <DragAndDropItemCard
                :item="item"
                :title="item.translatedType"
                height="400"
                :was-required="item.wasRequired"
                @deleteItem="deleteDocumentation(item)"
                @downloadFile="downloadDocumentation(item)"
                @viewFile="viewDocumentation(item)"
                @editItem="viewDocumentation(item)"
                :viewIconTooltip="$t('buttons:show')"
                :downloadIconTooltip="$t('buttons:download')"
                :description="cutFileName(item.name)"
                :additionalInfo="$t('book/public:qrcode.data.docus.item-card.valid') + ': ' + getValidity(item)"
                :hide-delete="!isLoggedIn"
            />
          </v-col>
        </v-row>
        <v-pagination v-show="this.restDocu.content.length != 0"
                      class="mt-3"
                      v-model="restDocu.options.page"
                      :length="totalPages"
                      :total-visible="7">
        </v-pagination>
      </div>
      <v-col v-if="restDocu.content.length == 0 && restRequiredDocus.length == 0" class="text-center">
        <h6 class="greyTint--text mb-0">
          {{ $t('common:no-data-found') }}
        </h6>
      </v-col>
      <v-col v-else-if="docuToggle !== 'ALL' && docuToggle !== 'DOCUMENTS' && restDocu.content.length == 0" class="text-center">
        <h6 class="greyTint--text mb-0">
          {{ $t('common:no-data-found') }}
        </h6>
      </v-col>
    </v-card>

    <!-- Dialog to upload documentation -->
    <v-dialog
        fullscreen
        eager
        v-model="dialog"
        hide-overlay
        content-class="form-dialog"
    >
      <v-card elevation="0">
        <v-col class="text-right px-4">
          <v-btn icon @click="closeDialog" ><v-icon large color="primary">close</v-icon></v-btn>
        </v-col>

        <v-card-text>
          <v-form ref="form">
            <v-row class="pa-5" justify="space-around">
              <v-col cols="12" lg="6">
                <h3 class="font-weight-bold">{{ $t('buttons:add-specific', { specific: $t('common:documentation')}) }}</h3>
                <DragAndDropFile
                    ref="dragAndDrop"
                    @fileChange="setFile($event)"
                    custom-height="75vh"
                />
              </v-col>

              <v-col cols="12" lg="5">
                <v-row>
                  <v-col cols="12" md="5">
                    <!-- Upload Section -->
                    <h4 class="primary--text font-weight-medium">{{ $t('buttons:upload') }}</h4>
                    <v-text-field
                        v-model="documentation.name"
                        outlined
                        :label="$t('common:document-name')"
                        :rules="[ruleRequired($t('utils:rules.required'))]"
                        tabindex="76"
                    />

                    <v-select
                        v-model="documentation.type"
                        outlined
                        :label="$t('book/public:qrcode.data.docus.type')"
                        :menu-props="{ bottom: true, offsetY: true }"
                        :items="restTypes"
                        item-text="translatedName"
                        item-value="name"
                        :rules="[ruleRequired($t('utils:rules.required'))]"
                        tabindex="77"
                    >
                    </v-select>
                  </v-col>
                  <v-col class="d-none d-md-block" md="1"></v-col>
                  <v-col cols="12" md="5">
                    <!-- Validity Section -->
                    <h4 class="primary--text font-weight-medium">{{ $t('book/public:qrcode.data.docus.date') }}</h4>

                    <DateField
                        ref="start"
                        @update="(v) => (documentation.start = v)"
                        :isClearable="true"
                        :label="$t('common:valid-from')"
                        tabindex="78"
                    />

                    <DateField
                        ref="end"
                        @update="(v) => (documentation.end = v)"
                        :isClearable="true"
                        :label="$t('common:valid-until')"
                        tabindex="79"
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <BaseButton
                      color="primary"
                      :btnText="$t('buttons:add-specific', { specific: $t('common:documentation') })"
                      @clickedButton="uploadFacilityDocumentation()"
                  />

                  <DirtyConfirmButtonDialog
                      ref="dirtyDialog"
                      :btn-text="$t('buttons:cancel')"
                      color="grey"
                      :dialog-title="$t('dialog:dirty-save-btn-title')"
                      :dialog-text="$t('dialog:dirty-save-btn-dialog')"
                      :button1-text="$t('dialog:dirty-save-btn-1-text')"
                      :button2-text="$t('dialog:dirty-save-btn-2-text')"
                      @clickedButton="closeDialog"
                      :dataToWatch="documentation"
                      hideOverlay
                  />
                </v-row>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <DocumentViewer ref="documentViewer" public/>

    <TwoButtonDialog
        ref="deleteDocumentationButton"
        :btn-text="$t('buttons:delete')"
        color="grey"
        :dialog-title="$t('dialog:delete-btn-title',{name : $t('book/public:qrcode.data.docus.delete-dialog-part-1')})"
        :dialog-text="$t('dialog:delete-btn-dialog',{name : $t('book/public:qrcode.data.docus.delete-dialog-part-2')})"
        :button1-text="$t('buttons:cancel')"
        :button2-text="$t('buttons:delete')"
        @clickedButton1="$refs.deleteDocumentationButton.showDialog = false"
        @clickedButton2="deleteDocumentationConfirmed()"
        :hide-button="true"
    />

  </div>
</template>
<script>
import TwoButtonDialog from "@/modules/core/components/TwoButtonDialog/TwoButtonDialog";
import DocumentViewer from "@/modules/core/components/DocumentViewer/DocumentViewer";
import BaseButton from "@/modules/core/components/Base/BaseButton";
import DataTablePaginated from "@/modules/core/components/DataTable/DataTablePaginated";
import RadialScore from "@/modules/core/components/RadialScore";
import Score from "@/modules/book/data/score";
import Utils from "@/modules/core/services/utils";
import Vue from "vue";
import DocumentationService from "@/modules/core/services/documentation-service";
import DataUtils from "@/modules/core/services/data-utils";
import DocumentationRule from "@/modules/book/data/documentation-rule";
import InfoService from "@/modules/core/services/info-service";
import {ruleRequired} from "@/modules/core/services/rules";
import DragAndDropFile from "@/modules/core/components/DragAndDropFile";
import DateField from "@/modules/core/components/DateField";
import DirtyConfirmButtonDialog from "@/modules/core/components/TwoButtonDialog/DirtyConfirmButtonDialog";
import FacilityDocumentation from "@/modules/book/data/facility-documentation";
import SearchAndSortBar from "@/modules/core/components/SearchAndSortBar";
import DragAndDropItemCard from "@/modules/core/components/DragAndDropItemCard";
import QRCodeService from "@/modules/book/services/qrcode-service";
import {mapGetters} from "vuex";

export default {
  name: "PublicDocuTab",
  components: {
    DragAndDropItemCard,
    SearchAndSortBar,
    DirtyConfirmButtonDialog,
    DateField,
    DragAndDropFile,
    RadialScore,
    DataTablePaginated,
    BaseButton,
    DocumentViewer,
    TwoButtonDialog
  },
  props: {
    facilityId: String
  },
  data() {
    return {
      restDocu: {
        options: [],
        search: [],
        filter: [],
        content: []
      },
      restRequiredDocus: [],
      cardView: true,
      totalPages: null,
      score: new Score(),
      dataFilters: [],
      docuToggle: 'ALL',

      // for dialog
      dialog: false,
      file: null,
      documentation: new FacilityDocumentation(),
      restTypes: []
    }
  },

  computed: {
    docuHeaders() {
      return [
        {text: this.$t("common:type"), value: "translatedType"},
        {text: this.$t("common:document-name"), value: "name"},
        {text: this.$t('common:valid-from'), value: 'startDate'},
        {text: this.$t('common:valid-until'), value: 'endDate'},
        {text: "", value: "controls", width: "190px"},
      ];
    },

    currentLang() {
      return this.$store.state.translation.language;
    },

    // return required documents if user is on page 1
    // so there wont be v-col problems when swithcing to a different page
    requiredDocus() {
      if (this.restDocu.options.page === 1) {
        return this.restRequiredDocus;
      } else {
        return []
      }
    },

    ...mapGetters('auth', ['isLoggedIn']),
  },

  watch: {
    currentLang() {
      Utils.sleep(500).then(() => {
        for (let i = 0; i < this.restDocu.content.length; i++) {
          this.restDocu.content[i].translatedType = this.$t('enums:facility-documentation-types.' + this.restDocu.content[i].type)
          Vue.set(this.restDocu.content, i, this.restDocu.content[i])
        }

        for (let i = 0; i < this.restRequiredDocus.length; i++) {
          this.restRequiredDocus[i].translatedName = this.$t('enums:facility-documentation-types.' + this.restRequiredDocus[i].name)
          Vue.set(this.restRequiredDocus, i, this.restRequiredDocus[i])
        }

        this.$refs.form.resetValidation();
      });
    },
    // when switching from tableView to cardView, refresh data
    cardView() {
      if (this.cardView) {
        this.updateDocuTable(this.restDocu.options, this.restDocu.search);
      }
    },

    // if docu toggle has changed, get the according filter and update the data with according filter
    docuToggle() {
      this.getFilters();
      this.restDocu.filter = "";
      this.updateDocuTable(this.restDocu.options, this.restDocu.search, this.restDocu.filter)
    },

    dialog() {
      if(this.dialog) {
        InfoService.readEnum("documentation/facility",
            (resp) => {
              for(var i = 0; i < resp.data.length; i++) {
                this.restTypes.push({name: resp.data[i]})
              }
              this.translateAndSortTypes()
              this.$refs.dirtyDialog.startWatch();
            },
            (error) => {
              this.$logger.error("Error at InfoService.readEnum: " + error);
              Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('book/public:qrcode.data.docus.no-docu-types')}))
            }
        )
      }
    }
  },

  methods: {
    ruleRequired,

    cutFileName(name) {
      return Utils.cropText(name, 28, false);
    },

    updateDocuTable(options, search, filter) {
      this.restDocu.options = options;
      this.restDocu.search = search;
      this.restDocu.filter = filter;

      if(!search) search = '';
      if (!filter) filter = '';
      if (!this.cardView) {
        this.$refs.tableDocuments.loading = true;
      }

      if (this.facilityId) {
        QRCodeService.readPageableFacilityDocumentationsFromAPI(
            this.$route.params.code,
            options.page - 1,
            options.itemsPerPage,
            search,
            filter,
            this.docuToggle,
            options.sortBy,
            options.sortDesc,
            (res) => {
              res.data.content.forEach((document) => {
                // set translated type for document
                document.translatedType = this.$t('enums:facility-documentation-types.' + document.type);
                document.src = '';
              })

              // we have to manually sort here, because we translate the enums in the frontend
              if (options.sortBy[0] === 'translatedType') {
                this.restDocu.content = Utils.sort(res.data.content, options.sortDesc[0], 'translatedType')
              } else {
                this.restDocu.content = res.data.content;
              }

              if (!this.cardView) {
                this.$refs.tableDocuments.fillTableData(res.data);
              } else {
                // if facilityType has required docus set
                if (this.restDocu.content.length > 0) {
                  for (let i = 0; i < this.restDocu.content.length; i++) {

                    // get file url to display a preview image of the document
                    DocumentationService.getDocByTypeAndIdForPublic(
                        this.restDocu.content[i].id,
                        this.restDocu.content[i].fileType,
                        this.restDocu.content[i].documentType,
                        this.restDocu.content[i].facilityId,
                        this.$route.params.code,
                        (result) => {
                          this.restDocu.content[i].src = result;

                        },
                        (error) => {
                          this.$logger.error("Failed to get documentation source, because: " + error);
                        }
                    )
                  }
                }
                this.getRequiredDocumentations()
                this.totalPages = res.data.totalPages;
              }
              this.getScore();
            },
            (error) => {
              if (!this.cardView) {
                this.$refs.tableDocuments.loading = false;
              }
              this.$logger.info("Error at fetching facility documentations: " + error);
              Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:documentation_plural')}))
            }
        )
      }
    },

    downloadDocumentation(item) {
      DocumentationService.downloadFileForPublic(item.id, item.fileName, item.fileType, item.documentType, item.facilityId, this.$route.params.code);
    },
    deleteDocumentation(item) {
      this.deleteDocumentationId = item.id;
      this.$refs.deleteDocumentationButton.showDialog = true;
    },
    deleteDocumentationConfirmed() {
      DocumentationService.delete(this.deleteDocumentationId,
          () => {
            this.updateDocuTable(this.restDocu.options, this.restDocu.search);
          },
          (error) => {
            this.$logger.error("Failed to delete user documentation, because: " + error);
            Utils.showFailureSnackbar(this.$t('snackbar:delete-failure', {name: this.$t('common:documentation')}))
          }
      )
      this.$refs.deleteDocumentationButton.showDialog = false;
    },

    viewDocumentation(item) {
      this.$refs.documentViewer.initViewerById(this.restDocu.content, item.id, item.facilityId, item.documentType);
    },

    getValidity(item) {
      if(item.startDate && item.endDate) {
        return item.startDate + ' - ' + item.endDate
      } else if(item.startDate && !item.endDate) {
        return this.$t('book/public:qrcode.data.docus.item-card.since') + ' ' + item.startDate
      } else if(!item.startDate && item.endDate) {
        return this.$t('book/public:qrcode.data.docus.item-card.till') + ' ' + item.endDate
      } else {
        return this.$t('book/public:qrcode.data.docus.item-card.valid-not-set')
      }
    },

    addRequiredDocu(file, item) {
      this.dialog = true;
      this.file = file;

      if (file) {
        let docuToParse = {
          fileType: file.type,
          src: URL.createObjectURL(file)
        }
        this.$refs.dragAndDrop.setPreviewSrc(docuToParse)

        // set name of documentation automatic from the filename
        this.documentation.name = file.name.split('.')[0];
        // set type of documentation automatically from the clicked req doc card
        if (item) {
          this.documentation.type = item.name;
        }
      } else {
        this.$refs.dragAndDrop.setPreviewSrc(null)
      }

      this.$refs.dragAndDrop.file = file;
      this.$refs.dragAndDrop.isUploading = false;
    },

    getRequiredDocumentations() {
      QRCodeService.readRequiredDocumentaitonsForFacilityFromAPI(this.facilityId,
          (res) => {
            this.restRequiredDocus = DataUtils.parseDataToObjectArray(res.data, DocumentationRule);
            for (let i = 0; i < this.restRequiredDocus.length; i++) {
              this.restRequiredDocus[i].translatedName = this.$t('enums:facility-documentation-types.' + this.restRequiredDocus[i].name);
            }
            Utils.sort(this.restRequiredDocus, true, 'priority')
          }
      )
    },

    getScore() {
      QRCodeService.readFacilityDocumentationScoreForFacility(this.facilityId,
          (res) => {
            this.score = DataUtils.parseDataToSingleObject(res.data, Score);
            this.$emit("updateDocuScore", this.score.percentage);
          }
      )
    },

    // get according filters depending which toggle button is selected
    getFilters() {
      if (this.docuToggle === 'ALL') {
        InfoService.readAndTranslateEnum(
            'documentation/facility/all',
            "enums:facility-documentation-types.",
            (res) => {
              this.dataFilters = Utils.sort(res, false, 'translatedName');
            }
        )
      } else if (this.docuToggle === 'DOCUMENTS') {
        InfoService.readAndTranslateEnum(
            'documentation/facility',
            "enums:facility-documentation-types.",
            (res) => {
              this.dataFilters = Utils.sort(res, false, 'translatedName');
            }
        )
      } else if (this.docuToggle === 'STATE_DOCUMENTS') {
        InfoService.readAndTranslateEnum(
            'documentation/facility/state',
            "enums:facility-documentation-types.",
            (res) => {
              this.dataFilters = Utils.sort(res, false, 'translatedName');
            }
        )
      } else if (this.docuToggle === 'IMAGES') {
        InfoService.readAndTranslateEnum(
            'documentation/facility/image',
            "enums:facility-documentation-types.",
            (res) => {
              this.dataFilters = Utils.sort(res, false, 'translatedName');
            }
        )
      }
    },

    setFile(item) {
      this.file = item;

      if (item) {
        let docuToParse = {
          fileType: item.type,
          src: URL.createObjectURL(item)
        }
        this.$refs.dragAndDrop.setPreviewSrc(docuToParse)
      } else {
        this.$refs.dragAndDrop.setPreviewSrc(null)
      }

      this.$refs.dragAndDrop.file = item;
      this.$refs.dragAndDrop.isUploading = false;
    },
    uploadFacilityDocumentation() {
      if (!this.$refs.form.validate()) {
        // TODO scroll to specific form field which doesnt require the rules
        window.scrollTo(0, 0);
        return;
      }

      if (this.documentation.start == null) {
        this.documentation.start = '';
      }
      if (this.documentation.end == null) {
        this.documentation.end = '';
      }

      QRCodeService.postFacilityDocumentationToAPI(this.facilityId, this.file, this.documentation,
          () => {
            Utils.showSuccessSnackbar(this.$t('snackbar:upload-success', {name: this.$t('common:document')}));
            this.updateDocuTable(this.restDocu.options, this.restDocu.search)
            this.closeDialog()
          },
          () => {
            Utils.showFailureSnackbar(this.$t('snackbar:upload-failure', {name: this.$t('common:document')}))
          })
    },

    translateAndSortTypes() {
      for(var j = 0; j < this.restTypes.length; j++) {
        this.restTypes[j].translatedName = this.$t('enums:facility-documentation-types.' + this.restTypes[j].name);
      }
      this.restTypes = Utils.sort(this.restTypes, false, "translatedName");
    },

    closeDialog() {
      this.dialog = false;
      this.$refs.form.reset();
      this.$refs.start.localDate = ''
      this.$refs.end.localDate = ''
    }
  },
  created() {
    this.getFilters()
  }
}
</script>